import { useState } from "react";
import { cn } from "../../utils/classes";
import styles from "./nzr-avatar.module.css";

/**
 * The ONE avatar shape every CSS-Modules-only surface in this app should
 * render with — a rounded square (design.md's "avatar-square" convention:
 * `rounded.xs`/6px, NOT a circle), image-with-initials-fallback, sized via
 * a plain `size` prop rather than a fixed set of variants so a 24px table
 * row and a 64px profile-page preview are the same component at different
 * sizes instead of two near-duplicate implementations drifting apart.
 *
 * Extracted from `../app-shell/identity-control/rail-avatar.tsx` (which
 * now wraps this for its own 24px glyph) — that component used to be the
 * only place this recipe existed, hardcoded to the rail's own size.
 *
 * Deliberately NOT `<AvatarV2>` from this same folder's `index.tsx` — that
 * component is styled entirely with Tailwind utility classes, which only
 * resolve to real CSS inside apps that run this repo's Tailwind pipeline
 * (see `rail-avatar.tsx`'s own doc comment for the live-confirmed failure
 * mode: an unstyled, unbordered box in a CSS-Modules-only app).
 */
export type NzrAvatarProps = {
  /** Used for the initials fallback (and, indirectly, its pastel background
   * color) when `imageUrl` is absent or fails to load. */
  name: string;
  imageUrl?: string | null;
  /** Both dimensions, in px. */
  size?: number;
  className?: string;
};

export function NzrAvatar({ name, imageUrl, size = 32, className }: NzrAvatarProps) {
  const [imageFailed, setImageFailed] = useState(false);
  const showImage = Boolean(imageUrl) && !imageFailed;
  const initials = getInitials(name);

  return (
    <span
      className={cn(styles.avatar, className)}
      style={{ width: size, height: size }}
      aria-hidden="true"
    >
      {showImage ? (
        <img
          className={styles.image}
          src={imageUrl!}
          alt=""
          onError={() => setImageFailed(true)}
        />
      ) : (
        <span
          className={styles.fallback}
          style={{
            backgroundColor: getColorFromInitials(initials),
            // Scales with size (rather than a fixed px value) so a 64px
            // profile preview's initials don't look tiny next to a 24px
            // rail glyph's — both read as "about 40% of the box".
            fontSize: Math.max(10, Math.round(size * 0.4)),
          }}
        >
          {initials}
        </span>
      )}
    </span>
  );
}

function getInitials(name: string): string {
  const chunks = (name ?? "").replace(/['"]/gu, "").trim().split(/\s+/);
  let initials = "";
  for (let i = 0; i < chunks.length && initials.length < 2; i++) {
    const chars = Array.from(chunks[i]);
    if (chars[0]) initials += chars[0];
  }
  return initials.toUpperCase();
}

// Same hash-to-pastel-hue approach `rail-avatar.tsx` used locally — always a
// light, fixed-lightness pastel regardless of light/dark theme, which is
// why `.fallback` below pins a fixed dark text color rather than `--ink`
// (which flips to near-white in dark mode and would lose contrast against
// this specific background).
function getColorFromInitials(initials: string): string {
  if (!initials) return "var(--card-2)";
  const chars = initials.slice(0, 2).padEnd(2, initials[0] || "A");
  const hash = (chars.charCodeAt(0) * 31 + chars.charCodeAt(1) * 17) % 360;
  return `hsl(${Math.abs(hash)}, 40%, 90%)`;
}
